import React, { useId, useState } from 'react';
import styled from 'styled-components';
import { DockedCardFloating } from '@repo/design-system';
import { space } from '@repo/design-tokens';
import { visuallyHidden } from '@repo/ui';

import { MovableCard, type CardInset } from './observerWorkspace/MovableCard';
import {
  useCardPositions,
  type CardId,
  type CardPositions,
} from './observerWorkspace/cardPositions';

/** The Observers cards' grip help, read by every plan card's grip (`MovableCard`). */
const GRIP_DESCRIPTION = 'Arrow keys move it. Home puts it back.';

/**
 * The ruled smallest height of a machine or area card (Rob's canvas; `floor-plan-item-cards`, R2).
 * Component geometry, not a token: where the plan card room is shorter, the card is the room's height.
 */
const ITEM_CARD_MIN_HEIGHT = '650px';

/** A plan card's minimum: the ruled height, or the room the layer leaves when that is shorter. */
const ITEM_CARD_MIN = `min(${ITEM_CARD_MIN_HEIGHT}, calc(100% - var(--plan-card-room, 2 * ${space[3]})))`;

/** What a plan card needs from its layer: `MovableCard`'s shared props. */
export interface PlanCardFrame {
  layer: HTMLDivElement | null;
  outlineHost: HTMLElement | null;
  positions: CardPositions;
  describedById: string;
  announce: (text: string) => void;
}

export interface PlanCardLayer {
  /** The layer element, once mounted: what a card moves in, and where a portalled card goes. */
  layerElement: HTMLDivElement | null;
  /** The props every plan card in this layer shares. */
  frame: PlanCardFrame;
  /** The layer itself, holding `children` (its cards). Pass it to `TwinPlanner`'s `canvasOverlay`. */
  render: (children: React.ReactNode) => React.ReactNode;
}

/**
 * The onboarding plan's card layer (`unified-camera-panel`, R1, R7), shared by Floor Plan and Camera
 * Feeds: one layer over the canvas that passes pointers through, the home outlines behind every card,
 * and the grip help. Positions are this person's, in the Observers cards' store (`useCardPositions`),
 * under the one `planCard` entry both steps share.
 *
 * The layer and its outline host are state, not refs: `MovableCard` measures against them before
 * paint, which a parent's ref would not yet hold.
 */
export const usePlanCardLayer = (
  sub: string | undefined,
  announce: (text: string) => void,
  /**
   * The cards' vertical clearance when a host moves their home off the default `space[3]` (Camera
   * Feeds, below the nav bar): a card taller than the room scrolls inside itself within it.
   */
  inset?: Pick<CardInset, 'top' | 'bottom'>,
  /**
   * Where the layer starts from the canvas's left edge, as a CSS length, when glass furniture sits
   * there (Camera Feeds' step list and stream list, `camera-feeds-card-clear-of-list`): a card's
   * travel, and a stored position at its left limit, then stay clear of it. Default `0`.
   */
  left?: string,
): PlanCardLayer => {
  const [layerElement, setLayerElement] = useState<HTMLDivElement | null>(null);
  const [outlineHost, setOutlineHost] = useState<HTMLDivElement | null>(null);
  const positions = useCardPositions(sub);
  const describedById = `${useId()}-grip-help`;
  const frame: PlanCardFrame = {
    layer: layerElement,
    outlineHost,
    positions,
    describedById,
    announce,
  };
  const render = (children: React.ReactNode) => (
    <Layer
      ref={setLayerElement}
      data-testid="plan-cards-layer"
      style={{
        ...(inset
          ? ({ '--plan-card-room': `${inset.top + inset.bottom}px` } as React.CSSProperties)
          : undefined),
        ...(left ? ({ '--plan-card-left': left } as React.CSSProperties) : undefined),
      }}
    >
      {/* Home outlines first, so every card paints over them. */}
      <div ref={setOutlineHost} />
      {children}
      <span id={describedById} style={visuallyHidden}>
        {GRIP_DESCRIPTION}
      </span>
    </Layer>
  );
  return { layerElement, frame, render };
};

export interface PlanCardProps {
  frame: PlanCardFrame;
  /**
   * The card's stored-position key. Default `planCard`, the one entry Floor Plan and Camera Feeds
   * share; Position twin's panel keeps its own (`onboarding-persistent-scene-position-twin`, R3.2).
   */
  id?: CardId;
  /** The card's name in *Move {name}* and *{Name} back in its corner*. */
  name: string;
  /** Its clearance from the canvas's edges (`MovableCard`'s `homeInset`). Default `space[3]`. */
  homeInset?: CardInset;
  /** Told whether the card is at home (`MovableCard`'s `onAtHomeChange`). */
  onAtHomeChange?: (atHome: boolean) => void;
  /** Told the card's measured size (`MovableCard`'s `onSize`). */
  onSize?: (size: { w: number; h: number }) => void;
  /**
   * The card is never shorter than 650 px, or the room when that is less (`floor-plan-item-cards`,
   * R2): the machine and area cards only, never the identify card or the camera panel.
   */
  minHeight?: boolean;
  children?: React.ReactNode;
}

/**
 * The one floating plan card: top-right over the canvas, moved by a grip in its head, sent home by
 * Home or a double-click, kept where this person left it. Every docked card inside it floats
 * (`DockedCardFloating`) and draws the grip first in its head.
 */
export const PlanCard: React.FC<PlanCardProps> = ({
  frame,
  id = 'planCard',
  name,
  homeInset,
  onAtHomeChange,
  onSize,
  minHeight = false,
  children,
}) => (
  <MovableCard
    id={id}
    home="top-right"
    name={name}
    homeInset={homeInset}
    onAtHomeChange={onAtHomeChange}
    onSize={onSize}
    minHeight={minHeight ? ITEM_CARD_MIN : undefined}
    {...frame}
  >
    {grip => <DockedCardFloating grip={grip}>{children}</DockedCardFloating>}
  </MovableCard>
);

/* Over the canvas; the cards take pointers back themselves (`MovableCard`). A card taller than the
   canvas scrolls inside itself. A card given a minimum (`PlanCard`'s `minHeight`) fills its frame, so
   its footer sits at the frame's foot. The card keeps its own width wherever it is placed: a positioned box
   otherwise shrinks to the room left of it, so a resize of the canvas would narrow a card at the
   right edge for a frame before it is re-placed, and re-measure it inside the same observation. */
const Layer = styled.div`
  position: absolute;
  inset: 0;
  left: var(--plan-card-left, 0);
  pointer-events: none;

  [data-movable-card] {
    width: max-content;
    max-height: calc(100% - var(--plan-card-room, 2 * ${space[3]}));
  }

  [data-movable-card][data-min-height] > * {
    flex: 1 1 auto;
  }
`;
